/**
 * Cross-tab signalling for the OAuth connect flow. The authorization happens in
 * a new tab; once the callback tab finalizes the connection it broadcasts the
 * result so the originating tab can refresh and select it without polling.
 */
const OAUTH_BROADCAST_CHANNEL = "oauth_connection";

/**
 * Payload broadcast from the OAuth callback tab to the tab that initiated the
 * connection flow.
 */
export interface OAuthConnectionMessage {
  providerId: string;
  connectionId: string;
}

/**
 * Broadcast a finalized OAuth connection to any listening tabs.
 * @param message - The provider and connection id that were just finalized.
 * @returns Nothing.
 */
export function broadcastOAuthConnection(
  message: OAuthConnectionMessage,
): void {
  const channel = new BroadcastChannel(OAUTH_BROADCAST_CHANNEL);
  channel.postMessage(message);
  channel.close();
}

/**
 * Subscribe to finalized OAuth connections broadcast from a callback tab.
 * @param onMessage - Handler invoked with each broadcast connection message.
 * @returns A cleanup function that closes the channel.
 */
export function subscribeToOAuthConnections(
  onMessage: (message: OAuthConnectionMessage) => void,
): () => void {
  const channel = new BroadcastChannel(OAUTH_BROADCAST_CHANNEL);
  const handler = (event: MessageEvent<OAuthConnectionMessage>) => {
    onMessage(event.data);
  };
  channel.addEventListener("message", handler);

  return () => {
    channel.removeEventListener("message", handler);
    channel.close();
  };
}
